import React, {useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {EZPRTheme} from '../../_theme';
import PropTypes from 'prop-types';
import {
    Autocomplete,
    CircularProgress,
    TextField,
    ThemeProvider,
    Popper,
    ButtonGroup,
    Button, Typography, Box,
} from '@mui/material';
import {ezprOrange} from '../../_helpers';
import {getAllL3CategoriesLambda, mlGetL3CategoryLambda} from '../../../utils/functionSignatures';
import {TreeView} from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
    removeL3Duplicates,
    buildSuggestionTree,
    buildAllTree,
    flattenTree,
    filterOptions,
    renderTreeNodes,
    getSelectedPath, pruneTree,
} from './l3CategoryUtils.jsx';
import {BrickContext} from 'olympe';

/**
 * @enum {string}
 */
L3CategoryDropdown.tabs = {
    SUGGESTION: 'Suggestion',
    ALL: 'All',
};

/**
 * @param {L3CategoryDropdown.propTypes} props
 * @return {JSX.Element}
 */
export function L3CategoryDropdown(props) {
    const style = {
        width: '100%',
        height: '100%',
    };

    const id = 'id-' + (Date.now() * Math.random());

    const [isPerformingQuery, setIsPerformingQuery] = useState(false);
    const [open, setOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState(L3CategoryDropdown.tabs.SUGGESTION);
    const [inputValue, setInputValue] = useState('');

    const [mlParsed, setMlParsed] = useState(null);
    const [allL3Categories, setAllL3Categories] = useState([]);

    const [expanded, setExpanded] = useState([]);

    const listboxRef = useRef(null);
    const scrollPositionRef = useRef(0);

    useEffect(() => {
        if (listboxRef.current) {
            listboxRef.current.scrollTop = scrollPositionRef.current;
        }
    }, [expanded]);

    // Fetch ML and All L3 categories
    useEffect(() => {
        const cleanedVendorCode = props.vendorCode == null ? '' : String(props.vendorCode).replace(/^0+/, '');

        if (isPerformingQuery) return;
        setIsPerformingQuery(true);

        const mlGetL3CategoryPromise = mlGetL3CategoryLambda(
            props.context,
            props.mlGetL3Category,
            props.companyCode,
            props.purchaseOrg,
            props.plant,
            cleanedVendorCode,
            props.costCenter,
            props.glAccount,
        );

        const getAllL3CategoriesPromise = getAllL3CategoriesLambda(
            props.context,
            props.getAllL3Categories,
        );

        Promise.allSettled([mlGetL3CategoryPromise, getAllL3CategoriesPromise])
            .then(([mlResult, allResult]) => {
                let uniqueL3s = [];

                if (mlResult.status === 'fulfilled') {
                    try {
                        const parsed = JSON.parse(mlResult.value);
                        setMlParsed(parsed);
                    } catch (parseError) {
                        console.error('Failed to parse ML L3 category response', parseError);
                    }
                } else {
                    console.error('ML L3 category fetch failed', mlResult.reason);
                }

                if (allResult.status === 'fulfilled') {
                    try {
                        const parsedAll = JSON.parse(allResult.value);
                        uniqueL3s = removeL3Duplicates(parsedAll);
                        setAllL3Categories(uniqueL3s);
                    } catch (parseError) {
                        console.error('Failed to parse All L3 categories response', parseError);
                    }
                } else {
                    console.error('All L3 categories fetch failed', allResult.reason);
                }
            })
            .finally(() => {
                setIsPerformingQuery(false);
            });
    }, [
        props.companyCode,
        props.purchaseOrg,
        props.plant,
        props.vendorCode,
        props.costCenter,
        props.glAccount,
    ]);

    useEffect(() => {
        if (!props.companyCode || !props.purchaseOrg || !props.plant) {
            props.onChange({label: '', value: ''});
        }
    }, [props.companyCode, props.purchaseOrg, props.plant]);

    const displayedOptions = useMemo(() => {
        if (!open) return [];

        const suggestionTree = mlParsed ?
            buildSuggestionTree(mlParsed, allL3Categories) :
            [];
        const allTree = buildAllTree(allL3Categories);
        const baseTree =
            currentTab === L3CategoryDropdown.tabs.SUGGESTION ?
                suggestionTree :
                allTree;

        if (inputValue) {
            return pruneTree(baseTree, inputValue);
        }
        return baseTree;
    }, [open, currentTab, mlParsed, allL3Categories, inputValue]);

    useEffect(() => {
        if (!open) return;

        if (inputValue) {
            const allIds = [];
            const walk = (nodes) => {
                nodes.forEach((n) => {
                    allIds.push(n.id);
                    if (n.children) walk(n.children);
                });
            };
            walk(displayedOptions);
            setExpanded(allIds);
        } else {
            setExpanded(Array.from(
                getSelectedPath(displayedOptions, props.selectedValue),
            ));
        }
    }, [open, inputValue, displayedOptions, props.selectedValue]);

    const selectedPathIds = useMemo(
        () => getSelectedPath(displayedOptions, props.selectedValue),
        [displayedOptions, props.selectedValue],
    );

    useEffect(() => {
        const pathArray = Array.from(selectedPathIds);
        if (pathArray.length > 0) {
            setExpanded(pathArray);
        }
    }, [selectedPathIds]);

    const handleToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    const handleChange = (_, newValue) => {
        if (!newValue) {
            props.onChange({label: '', value: ''});
        } else {
            props.onChange(newValue);
        }
    };

    const handleChangeTab = (tabName) => {
        setCurrentTab(tabName);
    };

    const popperWithTabs = useCallback(
        (params) => (
            <Popper
                {...params}
                className="custom-dropdown-menu-with-buttons"
            >
                <ButtonGroup color="primary" aria-label="outlined primary button group">
                    <Button
                        variant="text"
                        onClick={() => {
                            handleChangeTab(L3CategoryDropdown.tabs.SUGGESTION);
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{color: currentTab === L3CategoryDropdown.tabs.SUGGESTION ? ezprOrange : '#CCC'}}
                    >
                        {L3CategoryDropdown.tabs.SUGGESTION}
                    </Button>
                    <Button
                        data-cy={'l3_category_autocomplete_all_btn'}
                        variant="text"
                        onClick={() => handleChangeTab(L3CategoryDropdown.tabs.ALL)}
                        onMouseDown={(e) => e.preventDefault()}
                        style={{color: currentTab === L3CategoryDropdown.tabs.ALL ? ezprOrange : '#CCC'}}
                    >
                        {L3CategoryDropdown.tabs.ALL}
                    </Button>
                </ButtonGroup>
                {currentTab === L3CategoryDropdown.tabs.SUGGESTION && (
                    <Box sx={{px: 2, py: 1}}>
                        <Typography variant="body2" color="text.secondary">
                            The more you refine your need, the more accurate the suggestions will be.<br />
                            Criteria we use to refine the suggestions are: Company code, Purchasing Organisation, Plant, Supplier, Cost Center and GL Account.
                        </Typography>
                    </Box>
                )}

                {params.children}
            </Popper>
        ),
        [currentTab],
    );

    const handleOptionSelect = (option) => {
        props.onChange(option);
        setOpen(false);
    };

    const CustomListbox = React.forwardRef((listboxProps, ref) => (
        <div
            ref={(node) => {
                listboxRef.current = node;
                if (ref) {
                    if (typeof ref === 'function') {
                        ref(node);
                    } else {
                        ref.current = node;
                    }
                }
            }}
            {...listboxProps}
            style={{...listboxProps.style, overflowY: 'auto', overflowX: 'hidden'}}
        >
            <TreeView
                expanded={expanded}
                onNodeToggle={(event, nodeIds) => {
                    if (listboxRef.current) {
                        scrollPositionRef.current = listboxRef.current.scrollTop;
                    }
                    handleToggle(event, nodeIds);
                }}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
            >
                {renderTreeNodes(displayedOptions, selectedPathIds, handleOptionSelect)}
            </TreeView>
        </div>
    ));

    return (
        <ThemeProvider theme={EZPRTheme}>
            <Box mt="3px">
                <Autocomplete
                    inputValue={inputValue}
                    onInputChange={(e, newVal) => setInputValue(newVal)}
                    ListboxProps={{className: 'popup-scroller'}}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    data-cy={'l3_category_autocomplete'}
                    id={id}
                    filterOptions={filterOptions}
                    disabled={props.disabled}
                    PopperComponent={popperWithTabs}
                    options={flattenTree(displayedOptions)}
                    getOptionLabel={(option) => option.label}
                    fullWidth
                    loading={isPerformingQuery}
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => setOpen(false)}
                    style={style}
                    size="small"
                    value={props.selectedValue}
                    onChange={handleChange}
                    ListboxComponent={CustomListbox}
                    renderInput={(params) => (
                        <TextField
                            error={props.error}
                            {...params}
                            variant={'outlined'}
                            rows={1}
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        {isPerformingQuery && <CircularProgress color="inherit" size={20} />}
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                    renderOption={(liProps, option) => (
                        <li {...liProps} data-cy={'l3_category_option'}>
                            {option.label}
                        </li>
                    )}
                />
            </Box>
        </ThemeProvider>
    );
}

L3CategoryDropdown.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    selectedValue: PropTypes.object,
    onChange: PropTypes.func,
    mlGetL3Category: PropTypes.object,
    companyCode: PropTypes.string,
    purchaseOrg: PropTypes.string,
    plant: PropTypes.string,
    vendorCode: PropTypes.string,
    costCenter: PropTypes.string,
    glAccount: PropTypes.string,
    getAllL3Categories: PropTypes.object,
};
